import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { ReactTable } from './components/ReactTable/ReactTable';
import {ReactAccordion} from './components/ReactTable/ReactAccordion';
import { ReactButton } from './components/ReactTable/ReactButton';
import {ReactInput, ReactInputData} from './components/ReactTable/ReactInput';
import {ReactCheckbox, ReactCheckboxData} from './components/ReactTable/ReactCheckbox';
import {ReactLink, ReactLinkData} from "./components/ReactTable/ReactLink";
import {ReactAutoExpandTextArea, ReactAutoExpandTextAreaData} from "./components/ReactTable/ReactAutoExpandTextArea";
import { ReactSearchAsYouType } from './components/ReactTable/ReactSearchAsYouType';
import { ReactMultiSelectSearchAsYouType } from './components/ReactTable/ReactMultiSelectSearchAsYouType';
import {ReactTableShell} from "./components/ReactTable/ReactTableShell";
import {ReactSelect} from "./components/ReactTable/ReactSelect";
import {ReactSelectOption} from "./components/ReactTable/ReactSelectOption";
import {ReactCustomObject} from "./components/ReactTable/ReactCustomObject";
import {ReactHierarchicalList} from "./components/ReactTable/ReacthierarchicalList";
import {ReactJSONHierarchy} from "./components/ReactTable/ReactJSONHierarchy";
import {ECPHierarchyList, ECPHierarchyProps, createECPHierarchyList, getECPHierarchyList} from "./components/ReactTable/ECPHierarchyList";

/**
 * Creates an Orchard ReactTable on the element ID found
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function createReactTable(
    id: string,
    props: any,
    refCallback?: (component: ReactTable) => void
) {
     ReactDOM.render(
        getReactTable(props, refCallback),
        document.getElementById(id) as HTMLElement
    );
}

/**
 * Creates an Orchard ReactTable Object
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactTable(
    props: any,
    refCallback?: (component: ReactTable) => void
) {
    const setProps = {
        id: (null != props.id) ? props.id : '',
        rows: (null != props.rows) ? props.rows : [],
        headers: (null != props.headers) ? props.headers : [],
        headersCSS: (null != props.headersCSS) ? props.headersCSS : '',
        rowsCSS: (null != props.rowsCSS) ? props.rowsCSS : (rowObj : any, rowIndex: number, isDraggingRow: boolean) => {
                let rowClass = (rowIndex % 2 == 0) ? 'datatableRow selectableRow even' : 'datatableRow selectableRow odd';
                if(isDraggingRow) {
                    rowClass = rowClass + " react-drag";
                }
                return rowClass;
            },
        rowsStyle: (null != props.rowsStyle) ? props.rowsStyle : (rowObj : any, rowIndex: number) => {return {height: '21px'};},
        highlightSelectedRow: (null != props.highlightSelectedRow) ? props.highlightSelectedRow : 0, // -1 to turn off
        hightlightSelectedRowCSS: (null != props.hightlightSelectedRowCSS) ? props.hightlightSelectedRowCSS : (rowObj : any, rowIndex: number) => {return "copia_tableHighlightColor_b";},
        rowHasFocusCSS: (null != props.rowHasFocusCSS) ? props.rowHasFocusCSS : null,
        selectRecordWithRowClick: (null != props.selectRecordWithRowClick) ? props.selectRecordWithRowClick : false,
        selectRecordsWithRowDrag: (null != props.selectRecordsWithRowDrag) ? props.selectRecordsWithRowDrag : false,
        tableLabel: (null != props.tableLabel) ? props.tableLabel : '',
        selectColumns: (null != props.selectColumns) ? props.selectColumns : [],
        addSelectColumn: (null != props.addSelectColumn) ? props.addSelectColumn : false,
        selectColumnIndex: (null != props.selectColumnIndex) ? props.selectColumnIndex : 0, // -1 to add to the end
        toolBarDisplayHeader: (null != props.toolBarDisplayHeader) ? props.toolBarDisplayHeader : false, // If toolBarDisplayHeader and toolBarDisplayFooter are false then there is no limit, all rows will display
        toolBarDisplayFooter: (null != props.toolBarDisplayFooter) ? props.toolBarDisplayFooter : false,
        toolBarDisplayCompact: (null != props.toolBarDisplayCompact) ? props.toolBarDisplayCompact : false,
        toolBarDisplayTotalRowsSelected: (null != props.toolBarDisplayTotalRowsSelected) ? props.toolBarDisplayTotalRowsSelected : false,
        toolBarPageNumber: (null != props.toolBarPageNumber) ? props.toolBarPageNumber : 1,
        toolBarNumToDisplay: (null != props.toolBarNumToDisplay) ? props.toolBarNumToDisplay : 10,
        toolBarPrintListOnClick: (null != props.toolBarPrintListOnClick) ? props.toolBarPrintListOnClick : null,
        toolBarExportListOnClick: (null != props.toolBarExportListOnClick) ? props.toolBarExportListOnClick : null,
        toolBarManageColumnsOnClick: (null != props.toolBarManageColumnsOnClick) ? props.toolBarManageColumnsOnClick : null,
        toolBarStyle: (null != props.toolBarStyle) ? props.toolBarStyle : {minWidth: '525px'},
        toolBarCompactStyle: (null != props.toolBarStyle) ? props.toolBarStyle : {minWidth: '215px'},
        enableDragAndDrop: (null != props.enableDragAndDrop) ? props.enableDragAndDrop : false,
        sortColumnIndex: (null != props.sortColumnIndex) ? props.sortColumnIndex : -1, // -1 to add to the end
        onDropEvent: (null != props.onDropEvent) ? props.onDropEvent : null,
        onSortEvent: (null != props.onSortEvent) ? props.onSortEvent : null,
        css: (null != props.css) ? props.css : '',
        style: (null != props.style) ? props.style : {},
        divTableContainerCSS: (null != props.divTableContainerCSS) ? props.divTableContainerCSS : 'object-selection-table-parent overFlowAuto',
        tableStyle: (null != props.tableStyle) ? props.tableStyle : {},
        onRefresh: (null != props.onRefresh) ? props.onRefresh : null,
        toolBarRefreshID: (null != props.toolBarRefreshID) ? props.toolBarRefreshID : "",
        onToolBarRefresh: (null != props.onToolBarRefresh) ? props.onToolBarRefresh : null,
        disableID: (null != props.disableID) ? props.disableID : "",
        refreshID: (null != props.refreshID) ? props.refreshID : "",
        disabled: (null != props.disabled) ? props.disabled : false,
        disableHeaderScrolling: (null != props.disableHeaderScrolling) ? props.disableHeaderScrolling : false,
        onSelectCellClick: (null != props.onSelectCellClick) ? props.onSelectCellClick : null,
        selectHeaderClick: (null != props.selectHeaderClick) ? props.selectHeaderClick : null,
        clearSelectionClick: (null != props.clearSelectionClick) ? props.clearSelectionClick : null,
        addDeleteColumn: (null != props.addDeleteColumn) ? props.addDeleteColumn : false,
        deleteButtonStyle: (null != props.deleteButtonStyle) ? props.deleteButtonStyle : (rowObj : any, rowIndex: number) => {return {};},
        deleteButtonTabIndex: (null != props.deleteButtonTabIndex) ? props.deleteButtonTabIndex : null,
        deleteColumnIndex: (null != props.deleteColumnIndex) ? props.deleteColumnIndex : 0, // -1 to add to the end
        onDelete: (null != props.onDelete) ? props.onDelete : null,
        lockedRows: (null != props.lockedRows) ? props.lockedRows : [],
        disableRow: (null != props.disableRow) ? props.disableRow : null,
        disableDelete: (null != props.disableDelete) ? props.disableDelete : null,
        noRecordsSelectedText: (null != props.noRecordsSelectedText) ? props.noRecordsSelectedText : "No records selected",
        addCustomFilterOptions: (null != props.addCustomFilterOptions) ? props.addCustomFilterOptions : null,
        customFilterCSS: (null != props.customFilterCSS) ? props.customFilterCSS : "",
        customFilter: (null != props.customFilter) ? props.customFilter : null,
        addCustomToolBarOptions: (null != props.addCustomToolBarOptions) ? props.addCustomToolBarOptions : null,
        isSelectedKey: (null != props.isSelectedKey) ? props.isSelectedKey : "isSelected",
        customSortIndexKey: (null != props.customSortIndexKey) ? props.customSortIndexKey : "customSortIndex",
        onRowDoubleClick: (null != props.onRowDoubleClick) ? props.onRowDoubleClick : null,
        clearButtonTabIndex: (null != props.clearButtonTabIndex) ? props.clearButtonTabIndex : null,
        divSAYTWindowCSS: (null != props.divSAYTWindowCSS) ? props.divSAYTWindowCSS : null,
        divTableShellCSS: (null != props.divTableShellCSS) ? props.divTableShellCSS : null,
        divTableContainerShellCSS: (null != props.divTableContainerShellCSS) ? props.divTableContainerShellCSS : null,
        emptyRowCSS: (null != props.emptyRowCSS) ? props.emptyRowCSS : 'dataTables_empty',
        isInitialLoad: (null != props.isInitialLoad) ? props.isInitialLoad : () => {return false;},
        overrideInitialSortColumns: (null != props.overrideInitialSortColumns) ? props.overrideInitialSortColumns : "",
        overrideSortID: (null != props.overrideSortID) ? props.overrideSortID : "",
        overrideSortName: (null != props.overrideSortName) ? props.overrideSortName : "",
        overrideRowID: (null != props.overrideRowID) ? props.overrideRowID : null,
        overrideRowName: (null != props.overrideRowName) ? props.overrideRowName : null,
        deleteButtonID: (null != props.deleteButtonID) ? props.deleteButtonID : null,
        deleteButtonName: (null != props.deleteButtonName) ? props.deleteButtonName : null,
        selectCheckBoxID: (null != props.selectCheckBoxID) ? props.selectCheckBoxID : null,
        selectCheckBoxName: (null != props.selectCheckBoxName) ? props.selectCheckBoxName : null,
        overrideTableEntriesText: (null != props.overrideTableEntriesText) ? props.overrideTableEntriesText : "entries",
        overrideScrollIntoView: (null != props.overrideScrollIntoView) ? props.overrideScrollIntoView : null,
        searchValue: (null != props.searchValue) ? props.searchValue : "",
        toolBarDisplayTotalCSS: (null != props.toolBarDisplayTotalCSS) ? props.toolBarDisplayTotalCSS : 'lightGray2-bg ui-corner-tl ui-corner-tr',
        toolBarDisplayTotalStyle: (null != props.toolBarDisplayTotalStyle) ? props.toolBarDisplayTotalStyle : {border: '1px solid var(--lightGray2)', borderBottom: 0, color: "black", fontWeight: "bold", padding: "5px", position: "relative"},
        onSelectionChange: (null != props.onSelectionChange) ? props.onSelectionChange : null,
        overrideToolBarInfoText: (null != props.overrideToolBarInfoText) ? props.overrideToolBarInfoText : null,
        onTableScroll: (null != props.onTableScroll) ? props.onTableScroll : null,
        onKeyUp: (null != props.onKeyUp) ? props.onKeyUp : null,
        stopPropagationOnKeyPress: (null != props.stopPropagationOnKeyPress) ? props.stopPropagationOnKeyPress : null,
        onDropHeaderEvent: (null != props.onDropHeaderEvent) ? props.onDropHeaderEvent : null,
        deleteColumnHeader: (null != props.deleteColumnHeader) ? props.deleteColumnHeader : "",
        toolBarDisablePaging: (null != props.toolBarDisablePaging) ? props.toolBarDisablePaging : false,
        toolBarDisplayTotal: (null != props.toolBarDisplayTotal) ? props.toolBarDisplayTotal : null,
    };

    return <ReactTableShell {...setProps as any} ref={refCallback} />;
}


/**
 * Creates an Orchard ReactTable Object
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactTableLocal(
    props: any,
    refCallback?: (component: ReactTable) => void
) {
    const setProps = {
        id: (null != props.id) ? props.id : '',
        rows: (null != props.rows) ? props.rows : [],
        filteredRows: (null != props.filteredRows) ? props.filteredRows : [],
        headers: (null != props.headers) ? props.headers : [],
        headersCSS: (null != props.headersCSS) ? props.headersCSS : '',
        rowsCSS: (null != props.rowsCSS) ? props.rowsCSS : null,
        rowsStyle: (null != props.rowsStyle) ? props.rowsStyle : (rowObj : any, rowIndex: number) => {return {height: '21px'};},
        highlightSelectedRow: (null != props.highlightSelectedRow) ? props.highlightSelectedRow : 0, // -1 to turn off
        hightlightSelectedRowCSS: (null != props.hightlightSelectedRowCSS) ? props.hightlightSelectedRowCSS : (rowObj : any, rowIndex: number) => {return "copia_tableHighlightColor_b";},
        rowHasFocusCSS: (null != props.rowHasFocusCSS) ? props.rowHasFocusCSS : null,
        tableLabel: (null != props.tableLabel) ? props.tableLabel : '',
        showTable: (null != props.showTable) ? props.showTable : true,
        isSearchAsYouType: (null != props.isSearchAsYouType) ? props.isSearchAsYouType : false,
        hideSAYTOnSelect: (null != props.hideSAYTOnSelect) ? props.hideSAYTOnSelect : true,
        selectColumns: (null != props.selectColumns) ? props.selectColumns : [],
        onSelectCellClick: (null != props.onSelectCellClick) ? props.onSelectCellClick : null,
        addSelectColumn: (null != props.addSelectColumn) ? props.addSelectColumn : false,
        selectColumnIndex: (null != props.selectColumnIndex) ? props.selectColumnIndex : 0, // -1 to add to the end
        toolBarDisplayHeader: (null != props.toolBarDisplayHeader) ? props.toolBarDisplayHeader : false, // If toolBarDisplayHeader and toolBarDisplayFooter are false then there is no limit, all rows will display
        toolBarDisplayFooter: (null != props.toolBarDisplayFooter) ? props.toolBarDisplayFooter : false,
        toolBarDisplayCompact: (null != props.toolBarDisplayCompact) ? props.toolBarDisplayCompact : false,
        toolBarDisplayTotalRowsSelected: (null != props.toolBarDisplayTotalRowsSelected) ? props.toolBarDisplayTotalRowsSelected : false,
        toolBarPageNumber: (null != props.toolBarPageNumber) ? props.toolBarPageNumber : 1,
        toolBarNumToDisplay: (null != props.toolBarNumToDisplay) ? props.toolBarNumToDisplay : 10,
        toolBarPrintListOnClick: (null != props.toolBarPrintListOnClick) ? props.toolBarPrintListOnClick : null,
        toolBarExportListOnClick: (null != props.toolBarExportListOnClick) ? props.toolBarExportListOnClick : null,
        toolBarManageColumnsOnClick: (null != props.toolBarManageColumnsOnClick) ? props.toolBarManageColumnsOnClick : null,
        toolBarStyle: (null != props.toolBarStyle) ? props.toolBarStyle : {minWidth: '525px'},
        toolBarCompactStyle: (null != props.toolBarStyle) ? props.toolBarStyle : {minWidth: '215px'},
        enableDragAndDrop: (null != props.enableDragAndDrop) ? props.enableDragAndDrop : false,
        sortColumnIndex: (null != props.sortColumnIndex) ? props.sortColumnIndex : -1, // -1 to add to the end
        onDropEvent: (null != props.onDropEvent) ? props.onDropEvent : null,
        onDuringDragEvent: (null != props.onDuringDragEvent) ? props.onDuringDragEvent : null,
        onSortEvent: (null != props.onSortEvent) ? props.onSortEvent : null,
        css: (null != props.css) ? props.css : '',
        style: (null != props.style) ? props.style : {},
        toolBarUpdate: (null != props.toolBarUpdate) ? props.toolBarUpdate : null,
        sortUpdate: (null != props.sortUpdate) ? props.sortUpdate : null,
        onRefresh: (null != props.onRefresh) ? props.onRefresh : null,
        toolBarRefreshID: (null != props.toolBarRefreshID) ? props.toolBarRefreshID : "",
        onToolBarRefresh: (null != props.onToolBarRefresh) ? props.onToolBarRefresh : null,
        refreshID: (null != props.refreshID) ? props.refreshID : "",
        disabled: (null != props.disabled) ? props.disabled : false,
        disableHeaderScrolling: (null != props.disableHeaderScrolling) ? props.disableHeaderScrolling : false,
        selectHeaderClick: (null != props.selectHeaderClick) ? props.selectHeaderClick : null,
        clearSelectionClick: (null != props.clearSelectionClick) ? props.clearSelectionClick : null,
        addDeleteColumn: (null != props.addDeleteColumn) ? props.addDeleteColumn : false,
        deleteButtonStyle: (null != props.deleteButtonStyle) ? props.deleteButtonStyle : (rowObj : any, rowIndex: number) => {return {};},
        deleteColumnIndex: (null != props.deleteColumnIndex) ? props.deleteColumnIndex : 0, // -1 to add to the end
        onDelete: (null != props.onDelete) ? props.onDelete : null,
        lockedRows: (null != props.lockedRows) ? props.lockedRows : [],
        disableRow: (null != props.disableRow) ? props.disableRow : null,
        disableDelete: (null != props.disableDelete) ? props.disableDelete : null,
        toolBarNumToDisplayTyped: (null != props.toolBarNumToDisplay) ? props.toolBarNumToDisplay : 10,
        toolBarPageNumberTyped: (null != props.toolBarPageNumber) ? props.toolBarPageNumber : 1,
        noRecordsSelectedText: (null != props.noRecordsSelectedText) ? props.noRecordsSelectedText : "No records selected",
        addCustomFilterOptions: (null != props.addCustomFilterOptions) ? props.addCustomFilterOptions : null,
        customFilterCSS: (null != props.customFilterCSS) ? props.customFilterCSS : "",
        addCustomToolBarOptions: (null != props.addCustomToolBarOptions) ? props.addCustomToolBarOptions : null,
        deleteButtonTabIndex: (null != props.deleteButtonTabIndex) ? props.deleteButtonTabIndex : null,
        selectRecordWithRowClick: (null != props.selectRecordWithRowClick) ? props.selectRecordWithRowClick : false,
        selectRecordsWithRowDrag: (null != props.selectRecordsWithRowDrag) ? props.selectRecordsWithRowDrag : false,
        isSelectedKey: (null != props.isSelectedKey) ? props.isSelectedKey : "isSelected",
        onRowDoubleClick: (null != props.onRowDoubleClick) ? props.onRowDoubleClick : null,
        getTotalSelected: (null != props.getTotalSelected) ? props.getTotalSelected : null,
        clearButtonTabIndex: (null != props.clearButtonTabIndex) ? props.clearButtonTabIndex : null,
        onSAYTTableDisplayEvent: (null != props.onSAYTTableDisplayEvent) ? props.onSAYTTableDisplayEvent : null,
        divSAYTWindowCSS: (null != props.divSAYTWindowCSS) ? props.divSAYTWindowCSS : null,
        storedData: (null != props.storedData) ? props.storedData : null,
        useRowDataStorage: (null != props.useRowDataStorage) ? props.useRowDataStorage : false,
        dataKey: (null != props.dataKey) ? props.dataKey : "dataKey",
        hiddenSelectedKey: (null != props.hiddenSelectedKey) ? props.hiddenSelectedKey : null,
        hiddenSelectedKeyDelimiter: (null != props.hiddenSelectedKeyDelimiter) ? props.hiddenSelectedKeyDelimiter : "|",
        divTableShellCSS: (null != props.divTableShellCSS) ? props.divTableShellCSS : null,
        divTableContainerShellCSS: (null != props.divTableContainerShellCSS) ? props.divTableContainerShellCSS : null,
        emptyRowCSS: (null != props.emptyRowCSS) ? props.emptyRowCSS : 'dataTables_empty',
        overrideSortID: (null != props.overrideSortID) ? props.overrideSortID : "",
        overrideSortName: (null != props.overrideSortName) ? props.overrideSortName : "",
        storedDataAsJSONArray: (null != props.storedDataAsJSONArray) ? props.storedDataAsJSONArray : null,
        newJSONData: (null != props.newJSONData) ? props.newJSONData : null,
        overrideRowID: (null != props.overrideRowID) ? props.overrideRowID : null,
        overrideRowName: (null != props.overrideRowName) ? props.overrideRowName : null,
        deleteButtonID: (null != props.deleteButtonID) ? props.deleteButtonID : null,
        deleteButtonName: (null != props.deleteButtonName) ? props.deleteButtonName : null,
        selectCheckBoxID: (null != props.selectCheckBoxID) ? props.selectCheckBoxID : null,
        selectCheckBoxName: (null != props.selectCheckBoxName) ? props.selectCheckBoxName : null,
        overrideTableEntriesText: (null != props.overrideTableEntriesText) ? props.overrideTableEntriesText : "entries",
        overrideScrollIntoView: (null != props.overrideScrollIntoView) ? props.overrideScrollIntoView : null,
        searchValue: (null != props.searchValue) ? props.searchValue : "",
        toolBarDisplayTotalCSS: (null != props.toolBarDisplayTotalCSS) ? props.toolBarDisplayTotalCSS : 'lightGray2-bg ui-corner-tl ui-corner-tr',
        toolBarDisplayTotalStyle: (null != props.toolBarDisplayTotalStyle) ? props.toolBarDisplayTotalStyle : {border: '1px solid var(--lightGray2)', borderBottom: 0, color: "black", fontWeight: "bold", padding: "5px", position: "relative"},
        onSelectionChange: (null != props.onSelectionChange) ? props.onSelectionChange : null,
        overrideToolBarInfoText: (null != props.overrideToolBarInfoText) ? props.overrideToolBarInfoText : null,
        onTableScroll: (null != props.onTableScroll) ? props.onTableScroll : null,
        onKeyUp: (null != props.onKeyUp) ? props.onKeyUp : null,
        stopPropagationOnKeyPress: (null != props.stopPropagationOnKeyPress) ? props.stopPropagationOnKeyPress : null,
        onDropHeaderEvent: (null != props.onDropHeaderEvent) ? props.onDropHeaderEvent : null,
        onDuringDragHeader: (null != props.onDuringDragHeader) ? props.onDuringDragHeader : null,
        deleteColumnHeader: (null != props.deleteColumnHeader) ? props.deleteColumnHeader : "",
        updatePaging: (null != props.updatePaging) ? props.updatePaging : null,
        toolBarDisablePaging: (null != props.toolBarDisablePaging) ? props.toolBarDisablePaging : false,
        toolBarDisplayTotal: (null != props.toolBarDisplayTotal) ? props.toolBarDisplayTotal : null,
    };

    return <ReactTable {...setProps as any} ref={refCallback} />;
}

/**
 * Creates an Orchard ReactAccordion on the element ID found
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function createReactAccordion(
    id: string,
    props: any,
    refCallback?: (component: ReactAccordion) => void
) {
    ReactDOM.render(
        getReactAccordion(props, refCallback),
        document.getElementById(id) as HTMLElement
    );
}

/**
 * Gets an Orchard ReactAccordion Object
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactAccordion(
    props: any,
    refCallback?: (component: ReactAccordion) => void
) {
    const setProps = {
        id: (null != props.id) ? props.id : "",
        text: (null != props.text) ? props.text : '',
        content: (null != props.content) ? props.content : {},
        style: (null != props.style) ? props.style : {},
        collapsedStyle: (null != props.collapsedStyle) ? props.collapsedStyle : {margin: "0px"},
        collapsedCSS: (null != props.collapsedCSS) ? props.collapsedCSS : 'sectionHeader ui-accordion-header reactTableCell react-accordion-header react-rounded-bottom react-rounded-top',
        expandedCSS: (null != props.expandedCSS) ? props.expandedCSS : 'sectionHeader ui-accordion-header reactTableCell react-accordion-header react-rounded-top',
        expandedStyle: (null != props.expandedStyle) ? props.expandedStyle : {margin: "0px"},
        contentStyle: (null != props.contentStyle) ? props.contentStyle : {},
        contentCSS: (null != props.contentCSS) ? props.contentCSS : 'flex-container flex-column ui-accordion-content react-accordion-content react-rounded-bottom ui-accordion-content-active',
        dataKey: (null != props.dataKey) ? props.dataKey : "isExpanded",
        tabIndex: (null != props.tabIndex) ? props.tabIndex : null,
        row: (null != props.row) ? props.row : {},
        rowIndex: (null != props.rowIndex) ? props.rowIndex : -1,
        onClick: (null != props.onClick) ? props.onClick : (rowObj : any, shiftKey: boolean, ctrlKey: boolean, rowIndex: number) => {
            const dataKey = (null != props.dataKey) ? props.dataKey : "isExpanded";
            const isExpanded = null != rowObj[dataKey] && rowObj[dataKey];
            return !isExpanded;
        },
        refreshID: (null != props.refreshID) ? props.refreshID : "",
        onRefresh: (null != props.onRefresh) ? props.onRefresh : null,
        title: (null != props.title) ? props.title : "",
        expandedID: (null != props.expandedID) ? props.expandedID : "",
        stopPropagationOnClick: (null != props.stopPropagationOnClick) ? props.stopPropagationOnClick : null,
        disableID: (null != props.disableID) ? props.disableID : "",
        disabled: (null != props.disabled) ? props.disabled : false,
        arrowExpandCSS: (null != props.arrowExpandCSS) ? props.arrowExpandCSS : "ui-accordion-header-icon ui-icon ui-icon-triangle-1-s",
        arrowCollapseCSS: (null != props.arrowCollapseCSS) ? props.arrowCollapseCSS : "ui-accordion-header-icon ui-icon ui-icon-triangle-1-e",
        arrowStyle: (null != props.arrowStyle) ? props.arrowStyle : {marginTop: "1px"},
        enableDragAndDrop: (null != props.enableDragAndDrop) ? props.enableDragAndDrop : false,
        shellID: (null != props.shellID) ? props.shellID : "",
        onDragStart: (null != props.onDragStart) ? props.onDragStart : null,
        onDragStop: (null != props.onDragStop) ? props.onDragStop : null,
        onDuringDrag: (null != props.onDuringDrag) ? props.onDuringDrag : null,
        css: (null != props.css) ? props.css : "",
        allowExpandAndCollapse: (null != props.allowExpandAndCollapse) ? props.allowExpandAndCollapse : true,
        onWheel: (null != props.onWheel) ? props.onWheel : null,
        leftOfArrowContent: (null != props.leftOfArrowContent) ? props.leftOfArrowContent : null,
        doHighlight: (null != props.doHighlight) ? props.doHighlight : false,
        headerDivCSS: (null != props.headerDivCSS) ? props.headerDivCSS : "flex-container-inline fillSpace",
        textID: (null != props.textID) ? props.textID : null,
        onCollapseExpandAccordion: (null != props.onCollapseExpandAccordion) ? props.onCollapseExpandAccordion : null,
        selectRecordsWithRowDrag: (null != props.selectRecordsWithRowDrag) ? props.selectRecordsWithRowDrag : false,
        leftOfArrowJSONHierarchyContent: (null != props.leftOfArrowJSONHierarchyContent) ? props.leftOfArrowJSONHierarchyContent : null,
    };

    return <ReactAccordion {...setProps as any} ref={refCallback} />;
}

/**
 * Gets an Orchard ReactButton object
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactButton(
    props: any,
    refCallback?: (component: ReactButton) => void
) {
    const setProps = {
        id: (null != props.id) ? props.id : "",
        name: (null != props.name) ? props.name : "",
        text: (null != props.text) ? props.text : "",
        title: (null != props.title) ? props.title : "",
        css: (null != props.css) ? props.css : "",
        onClick: (null != props.onClick) ? props.onClick : null,
        row: (null != props.row) ? props.row : {},
        rowIndex: (null != props.rowIndex) ? props.rowIndex : -1,
        style: (null != props.style) ? props.style : {},
        disabled: (null != props.disabled) ? props.disabled : false,
        disableID: (null != props.disableID) ? props.disableID : "",
        tabIndex: (null != props.tabIndex) ? props.tabIndex : null,
        stopPropagationOnClick: (null != props.stopPropagationOnClick) ? props.stopPropagationOnClick : null,
        onKeyUp: (null != props.onKeyUp) ? props.onKeyUp : null,
        stopPropagationOnKeyPress: (null != props.stopPropagationOnKeyPress) ? props.stopPropagationOnKeyPress : null,
    };

    return <ReactButton {...setProps as any} ref={refCallback} />;
}

/**
 * Creates an Orchard ReactInput object
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactInput(
    props: any,
    refCallback?: (component: ReactInput) => void
) {

    const setProps = {
        id: (null != props.id) ? props.id : "",
        name: (null != props.name) ? props.name : "",
        row: (null != props.row) ? props.row : {},
        type: (null != props.type) ? props.type : "text",
        onChange: (null != props.onChange) ? props.onChange : null,
        onKeyDown: (null != props.onKeyDown) ? props.onKeyDown : null,
        onKeyUp: (null != props.onKeyUp) ? props.onKeyUp : null,
        onFocus: (null != props.onFocus) ? props.onFocus : null,
        onBlur: (null != props.onBlur) ? props.onBlur : null,
        rowIndex: (null != props.rowIndex) ? props.rowIndex : -1,
        style: (null != props.style) ? props.style : {},
        css: (null != props.css) ? props.css : '',
        disabled: (null != props.disabled) ? props.disabled : false,
        disableID: (null != props.disableID) ? props.disableID : "",
        refreshID: (null != props.refreshID) ? props.refreshID : "",
        onRefresh: (null != props.onRefresh) ? props.onRefresh : null,
        dataKey: (null != props.dataKey) ? props.dataKey : "dataKey",
        tabIndex: (null != props.tabIndex) ? props.tabIndex : null,
        title: (null != props.title) ? props.title : "",
        stopPropagationOnKeyPress: (null != props.stopPropagationOnKeyPress) ? props.stopPropagationOnKeyPress : null,
        autoComplete: (null != props.autoComplete) ? props.autoComplete : "off",
    } as ReactInputData;

    return <ReactInput {...setProps as any} ref={refCallback} />;
}

/**
 * Gets an Orchard ReactCheckbox object
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactCheckbox(
    props: any
) {
    const setProps = {
        id: (null != props.id) ? props.id : '',
        name: (null != props.name) ? props.name : '',
        onClick: (null != props.onClick) ? props.onClick : null,
        row: (null != props.row) ? props.row : {},
        rowIndex: (null != props.rowIndex) ? props.rowIndex : -1,
        disabled: (null != props.disabled) ? props.disabled : false,
        label: (null != props.label) ? props.label : '',
        css: (null != props.css) ? props.css : 'align-obj-center',
        style: (null != props.style) ? props.style : {},
        labelStyle: (null != props.labelStyle) ? props.labelStyle : {paddingBottom: '3px'},
        disableID: (null != props.disableID) ? props.disableID : "",
        dataKey: (null != props.dataKey) ? props.dataKey : "dataKey",
        tabIndex: (null != props.tabIndex) ? props.tabIndex : null,
        refreshID: (null != props.refreshID) ? props.refreshID : "",
        onRefresh: (null != props.onRefresh) ? props.onRefresh : null,
        title: (null != props.title) ? props.title : "",
        stopPropagationOnClick: (null != props.stopPropagationOnClick) ? props.stopPropagationOnClick : null,
        storedData: (null != props.storedData) ? props.storedData : null,
        useRowDataStorage: (null != props.useRowDataStorage) ? props.useRowDataStorage : false,
        hiddenSelectedKey: (null != props.hiddenSelectedKey) ? props.hiddenSelectedKey : null,
        hiddenSelectedKeyDelimiter: (null != props.hiddenSelectedKeyDelimiter) ? props.hiddenSelectedKeyDelimiter : "|",
        storedDataAsJSONArray: (null != props.storedDataAsJSONArray) ? props.storedDataAsJSONArray : null,
        newJSONData: (null != props.newJSONData) ? props.newJSONData : null,
        onKeyUp: (null != props.onKeyUp) ? props.onKeyUp : null,
        stopPropagationOnKeyPress: (null != props.stopPropagationOnKeyPress) ? props.stopPropagationOnKeyPress : null,
    } as ReactCheckboxData;

    return <ReactCheckbox {...setProps as any} />;
}

/**
 * Gets an Orchard ReactLink object
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactLink(
    props: any
) {
    const setProps = {
        id: (null != props.id) ? props.id : '',
        href: (null != props.href) ? props.href : '#',
        text: (null != props.text) ? props.text : '',
        style: (null != props.style) ? props.style : {},
        css: (null != props.css) ? props.css : '',
        row: (null != props.row) ? props.row : {},
        rowIndex: (null != props.rowIndex) ? props.rowIndex : -1,
        onClick: (null != props.onClick) ? props.onClick : null,
        disabled: (null != props.disabled) ? props.disabled : false,
        disableID: (null != props.disableID) ? props.disableID : "",
        tabIndex: (null != props.tabIndex) ? props.tabIndex : null,
        title: (null != props.title) ? props.title : "",
        stopPropagationOnClick: (null != props.stopPropagationOnClick) ? props.stopPropagationOnClick : null,
    } as ReactLinkData;

    return <ReactLink {...setProps as any} />;
}

/**
 * Gets an Orchard ReactAutoExpandTextArea object
 * @param props The properties object
 */
export function getReactAutoExpandTextArea(
    props: any
) {
    const setProps = {
        id: (null != props.id) ? props.id : '',
        text: (null != props.text) ? props.text : '',
        style: (null != props.style) ? props.style : {},
        css: (null != props.css) ? props.css : '',
        row: (null != props.row) ? props.row : {},
        rowIndex: (null != props.rowIndex) ? props.rowIndex : -1,
        onChange: (null != props.onChange) ? props.onChange : null,
        onBlur: (null != props.onBlur) ? props.onBlur : null,
        onFocus: (null != props.onFocus) ? props.onFocus : null,
        disabled: (null != props.disabled) ? props.disabled : false,
        disableID: (null != props.disableID) ? props.disableID : "",
        tabIndex: (null != props.tabIndex) ? props.tabIndex : null,
        title: (null != props.title) ? props.title : "",
        placeholder: (null != props.placeholder) ? props.placeholder : "",
        dataKey: (null != props.dataKey) ? props.dataKey : "dataKey",
        maxLength: (null != props.maxLength) ? props.maxLength : undefined,
        priority: (null != props.priority) ? props.priority : 'normal',
    } as ReactAutoExpandTextAreaData;

    return <ReactAutoExpandTextArea {...setProps as any} />;
}


/**
 * Gets an Orchard ReactSelect object
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactSelect(
    props: any,
    refCallback?: (component: ReactSelect) => void
) {
    const setProps = {
        id: (null != props.id) ? props.id : "",
        name: (null != props.name) ? props.name : "",
        customOption: (null != props.customOption) ? props.customOption : null,
        css: (null != props.css) ? props.css : "",
        onChange: (null != props.onChange) ? props.onChange : null,
        row: (null != props.row) ? props.row : {},
        rowIndex: (null != props.rowIndex) ? props.rowIndex : -1,
        dataKey: (null != props.dataKey) ? props.dataKey : "dataKey",
        style: (null != props.style) ? props.style : {},
        disabled: (null != props.disabled) ? props.disabled : false,
        disableID: (null != props.disableID) ? props.disableID : "",
        title: (null != props.title) ? props.title : "",
        onKeyDown: (null != props.onKeyDown) ? props.onKeyDown : null,
        stopPropagationOnKeyPress: (null != props.stopPropagationOnKeyPress) ? props.stopPropagationOnKeyPress : null,
        maxLength: (null != props.maxLength) ? props.maxLength : 4000,
    };

    return <ReactSelect {...setProps as any} ref={refCallback} />;
}

/**
 * Gets an Orchard ReactSelect object
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactSelectOption(
    props: any,
    refCallback?: (component: ReactSelectOption) => void
) {
    const setProps = {
        id: (null != props.id) ? props.id : "",
        css: (null != props.css) ? props.css : "",
        style: (null != props.style) ? props.style : {},
        disabled: (null != props.disabled) ? props.disabled : false,
        disableID: (null != props.disableID) ? props.disableID : "",
        value: (null != props.value) ? props.value : "",
        text: (null != props.text) ? props.text : "",
        title: (null != props.title) ? props.title : "",
        row: (null != props.row) ? props.row : {},
        dataKey: (null != props.dataKey) ? props.dataKey : "dataKey",
    };

    return <ReactSelectOption {...setProps as any} ref={refCallback} />;
}

/**
 * Creates an Orchard React SAYT on the element ID found
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function createReactSearchAsYouType(
    id: string,
    props: any,
    refCallback?: (component: ReactSearchAsYouType) => void
) {
    ReactDOM.render(
        getReactSearchAsYouType(props, refCallback),
        document.getElementById(id) as HTMLElement
    );
}

/**
 * Creates an Orchard React SAYT Object
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactSearchAsYouType(
    props: any,
    refCallback?: (component: ReactSearchAsYouType) => void
) {
    const setProps = {
        id: (null != props.id) ? props.id : '',
        label: (null != props.label) ? props.label : "",
        labelStyle: (null != props.labelStyle) ? props.labelStyle : {},
        labelCSS: (null != props.labelCSS) ? props.labelCSS : null,
        textStyle: (null != props.textStyle) ? props.textStyle : {},
        tableStyle: (null != props.tableStyle) ? props.tableStyle : {},
        headers: (null != props.headers) ? props.headers : [],
        headersCSS: (null != props.headersCSS) ? props.headersCSS : '',
        rowsCSS: (null != props.rowsCSS) ? props.rowsCSS : (rowObj : any, rowIndex: number) => {return (rowIndex % 2 == 0) ? 'datatableRow selectableRow even' : 'datatableRow selectableRow odd';},
        rowsStyle: (null != props.rowsStyle) ? props.rowsStyle : (rowObj : any, rowIndex: number) => {return {height: '21px'};},
        rows: (null != props.rows) ? props.rows : [],
        tabIndex: (null != props.tabIndex) ? props.tabIndex : null,
        divTableContainerCSS: (null != props.divTableContainerCSS) ? props.divTableContainerCSS : 'overFlowAuto',
        highlightSelectedRow: (null != props.highlightSelectedRow) ? props.highlightSelectedRow : 0, // -1 to turn off
        hightlightSelectedRowCSS: (null != props.hightlightSelectedRowCSS) ? props.hightlightSelectedRowCSS : (rowObj : any, rowIndex: number) => {return "copia_color3_b";},
        rowHasFocusCSS: (null != props.rowHasFocusCSS) ? props.rowHasFocusCSS : null,
        toolBarDisplayHeader: (null != props.toolBarDisplayHeader) ? props.toolBarDisplayHeader : false,
        toolBarDisplayFooter: (null != props.toolBarDisplayFooter) ? props.toolBarDisplayFooter : true,
        toolBarNumToDisplay: (null != props.toolBarNumToDisplay) ? props.toolBarNumToDisplay : 10,
        selectColumns: (null != props.selectColumns) ? props.selectColumns : [0],
        saytSelectDisplayColumn: (null != props.saytSelectDisplayColumn) ? props.saytSelectDisplayColumn : 0,
        onSAYTClickEvent: (null != props.onSAYTClickEvent) ? props.onSAYTClickEvent : null,
        hideSearchOnSelect: (null != props.hideSearchOnSelect) ? props.hideSearchOnSelect : true,
        showClearButton: (null != props.showClearButton) ? props.showClearButton : true,
        clearButtonTabIndex: (null != props.clearButtonTabIndex) ? props.clearButtonTabIndex : null,
        showSearchButton: (null != props.showSearchButton) ? props.showSearchButton : true,
        searchButtonTabIndex: (null != props.searchButtonTabIndex) ? props.searchButtonTabIndex : null,
        disabled: (null != props.disabled) ? props.disabled : false,
        disableHeaderScrolling: (null != props.disableHeaderScrolling) ? props.disableHeaderScrolling : false,
        disableID: (null != props.disableID) ? props.disableID : "",
        refreshID: (null != props.refreshID) ? props.refreshID : "",
        onRefresh: (null != props.onRefresh) ? props.onRefresh : null,
        toolBarRefreshID: (null != props.toolBarRefreshID) ? props.toolBarRefreshID : "",
        onToolBarRefresh: (null != props.onToolBarRefresh) ? props.onToolBarRefresh : null,
        noRecordsSelectedText: (null != props.noRecordsSelectedText) ? props.noRecordsSelectedText : "No records selected",
        addCustomFilterOptions: (null != props.addCustomFilterOptions) ? props.addCustomFilterOptions : null,
        customFilterCSS: (null != props.customFilterCSS) ? props.customFilterCSS : "reactTableFilter",
        customFilter: (null != props.customFilter) ? props.customFilter : null,
        onFocusOut: (null != props.onFocusOut) ? props.onFocusOut : null,
        selectRecordWithRowClick: (null != props.selectRecordWithRowClick) ? props.selectRecordWithRowClick : false,
        isSelectedKey: (null != props.isSelectedKey) ? props.isSelectedKey : "isSelected",
        onSortEvent: (null != props.onSortEvent) ? props.onSortEvent : null,
        showSearchButtonAfterClearButton: (null != props.showSearchButtonAfterClearButton) ? props.showSearchButtonAfterClearButton : true,
        onSAYTTableDisplayEvent: (null != props.onSAYTTableDisplayEvent) ? props.onSAYTTableDisplayEvent :
            (saytTableObj: any, saytInputObj: object) => {
                if (saytTableObj) {
                    saytTableObj.position({
                        my: 'left top',
                        at: 'left bottom+5',
                        of: saytInputObj,
                        collision: 'none'
                    });
                }
            },
        divSAYTWindowCSS: (null != props.divSAYTWindowCSS) ? props.divSAYTWindowCSS : null,
        onBeforeSAYTTableDisplayEvent: (null != props.onBeforeSAYTTableDisplayEvent) ? props.onBeforeSAYTTableDisplayEvent : null,
        onClearButtonClick: (null != props.onClearButtonClick) ? props.onClearButtonClick : null,
        allowFreeTextInput: (null != props.allowFreeTextInput) ? props.allowFreeTextInput : false,
        addCustomFreeTextRow: (null != props.addCustomFreeTextRow) ? props.addCustomFreeTextRow : null,
        isFreeTextKey: (null != props.isFreeTextKey) ? props.isFreeTextKey : "isFreeText",
        hiddenSelectedKey: (null != props.hiddenSelectedKey) ? props.hiddenSelectedKey : null,
        hiddenSelectedKeyID: (null != props.hiddenSelectedKeyID) ? props.hiddenSelectedKeyID : null,
        storedData: (null != props.storedData) ? props.storedData : null,
        dataKey: (null != props.dataKey) ? props.dataKey : "dataKey",
        css: (null != props.css) ? props.css : '',
        style: (null != props.style) ? props.style : {},
        title: (null != props.title) ? props.title : null,
        name: (null != props.name) ? props.name : "",
        noneSelectedText: (null != props.noneSelectedText) ? props.noneSelectedText : "",
        hiddenSelectedKeyName: (null != props.hiddenSelectedKeyName) ? props.hiddenSelectedKeyName : null,
        onFocusOutEvent: (null != props.onFocusOutEvent) ? props.onFocusOutEvent : null,
        onKeySelectionPressEvent: (null != props.onKeySelectionPressEvent) ? props.onKeySelectionPressEvent : null,
        hiddenTotalCountID: (null != props.hiddenTotalCountID) ? props.hiddenTotalCountID : ((null != props.id) ? props.id + '_totalCount' : ''),
        overrideRowID: (null != props.overrideRowID) ? props.overrideRowID : null,
        overrideRowName: (null != props.overrideRowName) ? props.overrideRowName : null,
        reselectOnReopen: (null != props.reselectOnReopen) ? props.reselectOnReopen : false,
        overrideTableEntriesText: (null != props.overrideTableEntriesText) ? props.overrideTableEntriesText : "entries",
        textCSS: (null != props.textCSS) ? props.textCSS : "searchAsYouTypeTextField",
        reselectIndexOnReopen: (null != props.reselectIndexOnReopen) ? props.reselectIndexOnReopen : null,
        selectIndexOnSearch: (null != props.selectIndexOnSearch) ? props.selectIndexOnSearch : null,
        overrideScrollIntoView: (null != props.overrideScrollIntoView) ? props.overrideScrollIntoView : null,
        overrideToolBarInfoText: (null != props.overrideToolBarInfoText) ? props.overrideToolBarInfoText : null,
        onKeyUp: (null != props.onKeyUp) ? props.onKeyUp : null,
        stopPropagationOnKeyPress: (null != props.stopPropagationOnKeyPress) ? props.stopPropagationOnKeyPress : null,
        noneSelectedTextStyle: (null != props.noneSelectedTextStyle) ? props.noneSelectedTextStyle : {},
        noneSelectedTextCSS: (null != props.noneSelectedTextCSS) ? props.noneSelectedTextCSS : '',
        autoComplete: (null != props.autoComplete) ? props.autoComplete : "off",
        hideTableWhenEmpty: (null != props.hideTableWhenEmpty) ? props.hideTableWhenEmpty : false,
        isInitialLoad: (null != props.isInitialLoad) ? props.isInitialLoad : () => {return false;},
        toolBarDisablePaging: (null != props.toolBarDisablePaging) ? props.toolBarDisablePaging : false,
        toolBarDisplayTotal: (null != props.toolBarDisplayTotal) ? props.toolBarDisplayTotal : null,
        onTableScroll: (null != props.onTableScroll) ? props.onTableScroll : null,
    };

    return <ReactSearchAsYouType {...setProps as any} ref={refCallback} />;
}

/**
 * Creates an Orchard Multiselect React SAYT on the element ID found
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function createReactMultiSelectSearchAsYouType(
    id: string,
    props: any,
    refCallback?: (component: ReactMultiSelectSearchAsYouType) => void
) {
    ReactDOM.render(
        getReactMultiSelectSearchAsYouType(props, refCallback),
        document.getElementById(id) as HTMLElement
    );
}


/**
 * Creates an Orchard Multiselect React SAYT Object
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactMultiSelectSearchAsYouType(
    props: any,
    refCallback?: (component: ReactMultiSelectSearchAsYouType) => void
) {
    const setProps = {
        id: (null != props.id) ? props.id : '',
        label: (null != props.label) ? props.label : "",
        tableLabel: (null != props.tableLabel) ? props.tableLabel : '',
        style: (null != props.style) ? props.style : {},
        labelStyle: (null != props.labelStyle) ? props.labelStyle : {},
        labelCSS: (null != props.labelCSS) ? props.labelCSS : null,
        textStyle: (null != props.textStyle) ? props.textStyle : {},
        saytTableStyle: (null != props.saytTableStyle) ? props.saytTableStyle : {},
        tableStyle: (null != props.tableStyle) ? props.tableStyle : {},
        headers: (null != props.headers) ? props.headers : [],
        headersCSS: (null != props.headersCSS) ? props.headersCSS : '',
        rowsCSS: (null != props.rowsCSS) ? props.rowsCSS : (rowObj : any, rowIndex: number) => {return (rowIndex % 2 == 0) ? 'datatableRow selectableRow even' : 'datatableRow selectableRow odd';},
        rowsStyle: (null != props.rowsStyle) ? props.rowsStyle : (rowObj : any, rowIndex: number) => {return {height: '28px'};},
        selectionRowsCSS: (null != props.selectionRowsCSS) ? props.selectionRowsCSS : (rowObj : any, rowIndex: number) => {return (rowIndex % 2 == 0) ? 'datatableRow selectableRow even' : 'datatableRow selectableRow odd';},
        selectionRowsStyle: (null != props.selectionRowsStyle) ? props.selectionRowsStyle : (rowObj : any, rowIndex: number) => {return {height: '28px'};},
        rows: (null != props.rows) ? props.rows : [],
        tabIndex: (null != props.tabIndex) ? props.tabIndex : null,
        selectionHeaders: (null != props.selectionHeaders) ? props.selectionHeaders : ((props.headers) ? props.headers : []),
        selectionHeadersCSS: (null != props.selectionHeadersCSS) ? props.selectionHeadersCSS : '',
        css: (null != props.css) ? props.css : "mutliSelectSAYTBorder",
        divTableContainerCSS: (null != props.divTableContainerCSS) ? props.divTableContainerCSS : 'object-selection-table-parent overFlowAuto',
        divSAYTTableContainerCSS: (null != props.divSAYTTableContainerCSS) ? props.divSAYTTableContainerCSS : 'overFlowAuto',
        highlightSelectedRow: (null != props.highlightSelectedRow) ? props.highlightSelectedRow : 0, // -1 to turn off
        hightlightSelectedRowCSS: (null != props.hightlightSelectedRowCSS) ? props.hightlightSelectedRowCSS : (rowObj : any, rowIndex: number) => {return "copia_color3_b";},
        rowHasFocusCSS: (null != props.rowHasFocusCSS) ? props.rowHasFocusCSS : null,
        toolBarDisplayHeader: (null != props.toolBarDisplayHeader) ? props.toolBarDisplayHeader : false,
        toolBarDisplayFooter: (null != props.toolBarDisplayFooter) ? props.toolBarDisplayFooter : true,
        toolBarNumToDisplay: (null != props.toolBarNumToDisplay) ? props.toolBarNumToDisplay : 10,
        selectColumns: (null != props.selectColumns) ? props.selectColumns : [0],
        onSAYTClickEvent: (null != props.onSAYTClickEvent) ? props.onSAYTClickEvent : null,
        hideSearchOnSelect: (null != props.hideSearchOnSelect) ? props.hideSearchOnSelect : false,
        showSearchButton: (null != props.showSearchButton) ? props.showSearchButton : true,
        searchButtonTabIndex: (null != props.searchButtonTabIndex) ? props.searchButtonTabIndex : null,
        disabled: (null != props.disabled) ? props.disabled : false,
        disableHeaderScrolling: (null != props.disableHeaderScrolling) ? props.disableHeaderScrolling : false,
        disableID: (null != props.disableID) ? props.disableID : "",
        refreshID: (null != props.refreshID) ? props.refreshID : "",
        refreshSAYTTableID: (null != props.refreshSAYTTableID) ? props.refreshSAYTTableID : "",
        onRefresh: (null != props.onRefresh) ? props.onRefresh : null,
        toolBarRefreshID: (null != props.toolBarRefreshID) ? props.toolBarRefreshID : "",
        onToolBarRefresh: (null != props.onToolBarRefresh) ? props.onToolBarRefresh : null,
        noRecordsSelectedText: (null != props.noRecordsSelectedText) ? props.noRecordsSelectedText : "No records selected",
        addCustomFilterOptions: (null != props.addCustomFilterOptions) ? props.addCustomFilterOptions : null,
        customFilterCSS: (null != props.customFilterCSS) ? props.customFilterCSS : "reactTableFilter",
        customFilter: (null != props.customFilter) ? props.customFilter : null,
        onFocusOut: (null != props.onFocusOut) ? props.onFocusOut : null,
        selectRecordWithRowClick: (null != props.selectRecordWithRowClick) ? props.selectRecordWithRowClick : false,
        isSelectedKey: (null != props.isSelectedKey) ? props.isSelectedKey : "isSelected",
        onSortEvent: (null != props.onSortEvent) ? props.onSortEvent : null,
        selectHeaderClick: (null != props.selectHeaderClick) ? props.selectHeaderClick : null,
        onSAYTTableDisplayEvent: (null != props.onSAYTTableDisplayEvent) ? props.onSAYTTableDisplayEvent :
            (saytTableObj: any, saytInputObj: object) => {
                if (saytTableObj) {
                    saytTableObj.position({
                        my: 'left top',
                        at: 'left bottom+5',
                        of: saytInputObj,
                        collision: 'none'
                    });
                }
            },
        divSAYTWindowCSS: (null != props.divSAYTWindowCSS) ? props.divSAYTWindowCSS : null,
        onBeforeSAYTTableDisplayEvent: (null != props.onBeforeSAYTTableDisplayEvent) ? props.onBeforeSAYTTableDisplayEvent : null,
        hiddenSelectedKey: (null != props.hiddenSelectedKey) ? props.hiddenSelectedKey : null,
        hiddenSelectedKeyID: (null != props.hiddenSelectedKeyID) ? props.hiddenSelectedKeyID : null,
        hiddenSelectedKeyDelimiter: (null != props.hiddenSelectedKeyDelimiter) ? props.hiddenSelectedKeyDelimiter : "|",
        storedData: (null != props.storedData) ? props.storedData : null,
        dataKey: (null != props.dataKey) ? props.dataKey : "dataKey",
        hiddenSelectedKeyName: (null != props.hiddenSelectedKeyName) ? props.hiddenSelectedKeyName : null,
        storedDataAsJSONArray: (null != props.storedDataAsJSONArray) ? props.storedDataAsJSONArray : null,
        newJSONData: (null != props.newJSONData) ? props.newJSONData : null,
        onFocusOutEvent: (null != props.onFocusOutEvent) ? props.onFocusOutEvent : null,
        overrideRowID: (null != props.overrideRowID) ? props.overrideRowID : null,
        overrideRowName: (null != props.overrideRowName) ? props.overrideRowName : null,
        selectCheckBoxID: (null != props.selectCheckBoxID) ? props.selectCheckBoxID : null,
        selectCheckBoxName: (null != props.selectCheckBoxName) ? props.selectCheckBoxName : null,
        tableOverrideRowID: (null != props.tableOverrideRowID) ? props.tableOverrideRowID : null,
        tableOverrideRowName: (null != props.tableOverrideRowName) ? props.tableOverrideRowName : null,
        tableSelectCheckBoxID: (null != props.tableSelectCheckBoxID) ? props.tableSelectCheckBoxID : null,
        tableSelectCheckBoxName: (null != props.tableSelectCheckBoxName) ? props.tableSelectCheckBoxName : null,
        overrideTableEntriesText: (null != props.overrideTableEntriesText) ? props.overrideTableEntriesText : "entries",
        hiddenTotalCountID: (null != props.hiddenTotalCountID) ? props.hiddenTotalCountID : ((null != props.id) ? props.id + '_totalCount' : ''),
        onKeySelectionPressEvent: (null != props.onKeySelectionPressEvent) ? props.onKeySelectionPressEvent : null,
        textCSS: (null != props.textCSS) ? props.textCSS : "searchAsYouTypeTextField",
        title: (null != props.title) ? props.title : null,
        overrideScrollIntoView: (null != props.overrideScrollIntoView) ? props.overrideScrollIntoView : null,
        tableoverrideScrollIntoView: (null != props.tableoverrideScrollIntoView) ? props.tableoverrideScrollIntoView : null,
        overrideToolBarInfoText: (null != props.overrideToolBarInfoText) ? props.overrideToolBarInfoText : null,
        onKeyUp: (null != props.onKeyUp) ? props.onKeyUp : null,
        stopPropagationOnKeyPress: (null != props.stopPropagationOnKeyPress) ? props.stopPropagationOnKeyPress : null,
        autoComplete: (null != props.autoComplete) ? props.autoComplete : "off",
        isInitialLoad: (null != props.isInitialLoad) ? props.isInitialLoad : () => {return false;},
        toolBarDisablePaging: (null != props.toolBarDisablePaging) ? props.toolBarDisablePaging : false,
        toolBarDisplayTotal: (null != props.toolBarDisplayTotal) ? props.toolBarDisplayTotal : null,
        onTableScroll: (null != props.onTableScroll) ? props.onTableScroll : null,
    };

    return <ReactMultiSelectSearchAsYouType {...setProps as any} ref={refCallback} />
}


/**
 * Creates an Orchard ReactCustomObject on the element ID found
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function createReactCustomObject(
    id: string,
    props: any,
    refCallback?: (component: ReactCustomObject) => void
) {
    ReactDOM.render(
        getReactCustomObject(props, refCallback),
        document.getElementById(id) as HTMLElement
    );
}

/**
 * Gets an Orchard ReactCustomObject Object
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactCustomObject(
    props: any,
    refCallback?: (component: ReactCustomObject) => void
) {
    const setProps = {
        id: (null != props.id) ? props.id : '',
        row: (null != props.row) ? props.row : {},
        index: (null != props.index) ? props.index : -1,
        refreshID: (null != props.refreshID) ? props.refreshID : "",
        onRefresh: (null != props.onRefresh) ? props.onRefresh : null,
        displayOverride: (null != props.displayOverride) ? props.displayOverride : null,
        appendJQueryObj: (null != props.appendJQueryObj) ? props.appendJQueryObj : null,
        disabled: (null != props.disabled) ? props.disabled : false,
        disableID: (null != props.disableID) ? props.disableID : "",
        style: (null != props.style) ? props.style : {},
        css: (null != props.css) ? props.css : '',
        JQueryObjectStyle: (null != props.JQueryObjectStyle) ? props.JQueryObjectStyle : {},
        JQueryObjectCSS: (null != props.JQueryObjectCSS) ? props.JQueryObjectCSS : '',
        displayObjectStyle: (null != props.displayObjectStyle) ? props.displayObjectStyle : {},
        displayObjectCSS: (null != props.displayObjectCSS) ? props.displayObjectCSS : '',
        rowIndex: (null != props.rowIndex) ? props.rowIndex : -1,
        onClick: (null != props.onClick) ? props.onClick : null,
        stopPropagationOnClick: (null != props.stopPropagationOnClick) ? props.stopPropagationOnClick : false,
        onKeyDown: (null != props.onKeyDown) ? props.onKeyDown : null,
        stopPropagationOnKeyPress: (null != props.stopPropagationOnKeyPress) ? props.stopPropagationOnKeyPress : null,
    };

    return <ReactCustomObject {...setProps as any} ref={refCallback} />;
}

/**
 * Creates an Orchard ReactHierarchicalList on the element ID found
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function createReactHierarchicalList(
    id: string,
    props: any,
    refCallback?: (component: ReactHierarchicalList) => void
) {
    ReactDOM.render(
        getReactHierarchicalList(props, refCallback),
        document.getElementById(id) as HTMLElement
    );
}

/**
 * Gets an Orchard ReactHierarchicalList Object
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactHierarchicalList(
    props: any,
    refCallback?: (component: ReactHierarchicalList) => void
) {
    const setProps = {
        id: (null != props.id) ? props.id : "",
        hierarchyKey: (null != props.hierarchyKey) ? props.hierarchyKey : "",
        style: (null != props.style) ? props.style : {margin: "0px"},
        css: (null != props.css) ? props.css : 'react-hierarchy',
        divContainerCSS: (null != props.divContainerCSS) ? props.divContainerCSS : 'overFlowAuto',
        divContainerStyle: (null != props.divContainerStyle) ? props.divContainerStyle : {},
        expandedCSS: (null != props.expandedCSS) ? props.expandedCSS : 'flex-container-inline',
        expandedStyle: (null != props.expandedStyle) ? props.expandedStyle : {marginBottom: "0px", marginTop: "0px", fontWeight: "normal"},
        contentStyle: (null != props.contentStyle) ? props.contentStyle : (rowObj : any, rowIndex: number) => {return {marginTop: '0px', paddingLeft: '16px', fontWeight: "normal"};},
        contentCSS: (null != props.contentCSS) ? props.contentCSS : '',
        dataKey: (null != props.dataKey) ? props.dataKey : "isExpanded",
        tabIndex: (null != props.tabIndex) ? props.tabIndex : null,
        refreshID: (null != props.refreshID) ? props.refreshID : "",
        onRefresh: (null != props.onRefresh) ? props.onRefresh : null,
        expandedID: (null != props.expandedID) ? props.expandedID : "",
        stopPropagationOnClick: (null != props.stopPropagationOnClick) ? props.stopPropagationOnClick : false,
        disableID: (null != props.disableID) ? props.disableID : "",
        rowID: (null != props.rowID) ? props.rowID : "",
        deleteButtonID: (null != props.deleteButtonID) ? props.deleteButtonID : "",
        hiddenTotalCountID: (null != props.hiddenTotalCountID) ? props.hiddenTotalCountID : "",
        disabled: (null != props.disabled) ? props.disabled : false,
        arrowExpandCSS: (null != props.arrowExpandCSS) ? props.arrowExpandCSS : "ui-accordion-header-icon ui-icon ui-icon-triangle-1-s",
        arrowCollapseCSS: (null != props.arrowCollapseCSS) ? props.arrowCollapseCSS : "ui-accordion-header-icon ui-icon ui-icon-triangle-1-e",
        arrowStyle: (null != props.arrowStyle) ? props.arrowStyle : (rowObj : any, rowIndex: number) => {return {width: "16px", marginTop: "1px"};},
        isSelectedKey: (null != props.isSelectedKey) ? props.isSelectedKey : "isSelected",
        displayKey: (null != props.displayKey) ? props.displayKey : "",
        displayOverride: (null != props.displayOverride) ? props.displayOverride : null,
        contentOverride: (null != props.contentOverride) ? props.contentOverride : null,
        rows: (null != props.rows) ? props.rows : [],
        highlightOnClick: (null != props.highlightOnClick) ? props.highlightOnClick : false,
        hightlightSelectedRowCSS: (null != props.hightlightSelectedRowCSS) ? props.hightlightSelectedRowCSS : (rowObj : any, rowIndex: number) => {return "copia_color3_b";},
        rowAdditionalAttributes: (null != props.rowAdditionalAttributes) ? props.rowAdditionalAttributes : (rowObj : any, rowIndex: number) => {return {};},
        collapsedStyle: (null != props.collapsedStyle) ? props.collapsedStyle : {marginBottom: "0px", marginTop: "0px", fontWeight: "normal"},
        collapsedCSS: (null != props.collapsedCSS) ? props.collapsedCSS : "flex-container-inline",
        rowsCSS: (null != props.rowsCSS) ? props.rowsCSS : (rowObj : any, rowIndex: number) => {return 'flex-container-inline';},
        rowsStyle: (null != props.rowsStyle) ? props.rowsStyle : (rowObj : any, rowIndex: number) => {return {height: '16px'};},
        onRowClick: (null != props.onRowClick) ? props.onRowClick : (rowObj : any, shiftKey: boolean, ctrlKey: boolean, rowIndex: number) => {
            const dataKey = (null != props.dataKey) ? props.dataKey : "isExpanded";
            const isExpanded = null != rowObj[dataKey] && rowObj[dataKey];
            return !isExpanded;
        },
        onRowRightClick: (null != props.onRowRightClick) ? props.onRowRightClick : null,
        onKeyDown: (null != props.onKeyDown) ? props.onKeyDown : null,
        onRowDelete: (null != props.onRowDelete) ? props.onRowDelete : null,
        onAfterRowDelete: (null != props.onAfterRowDelete) ? props.onAfterRowDelete : null,
        addDeleteButton: (null != props.addDeleteButton) ? props.addDeleteButton : false,
        deleteButtonTabIndex: (null != props.deleteButtonTabIndex) ? props.deleteButtonTabIndex : null,
        addSortIcon: (null != props.addSortIcon) ? props.addSortIcon : false,
        addDropIcon: (null != props.addDropIcon) ? props.addDropIcon : false,
        enableDragAndDrop: (null != props.enableDragAndDrop) ? props.enableDragAndDrop : false,
        onDropEvent: (null != props.onDropEvent) ? props.onDropEvent : null,
        enableUnboundDragAndDrop: (null != props.enableUnboundDragAndDrop) ? props.enableUnboundDragAndDrop : false,
        allowDrag: (null != props.allowDrag) ? props.allowDrag : null,
        allowDrop: (null != props.allowDrop) ? props.allowDrop : null,
        allowDropToTheBottom: (null != props.allowDropToTheBottom) ? props.allowDropToTheBottom : true,
        allowExpandAndCollapse: (null != props.allowExpandAndCollapse) ? props.allowExpandAndCollapse : (rowObj : any, rowIndex: number) => {return true;},
        shouldRowUpdate: (null != props.shouldRowUpdate) ? props.shouldRowUpdate : null,
        initialRowSelected: (null != props.initialRowSelected) ? props.initialRowSelected : "",
        hierarchyNumToDisplay: (null != props.hierarchyNumToDisplay) ? props.hierarchyNumToDisplay : -1,
        rowKey: (null != props.rowKey) ? props.rowKey : null,
        onFocusEndKeyPressEvent: (null != props.onFocusEndKeyPressEvent) ? props.onFocusEndKeyPressEvent : null,
        allowFocusOnRow: (null != props.allowFocusOnRow) ? props.allowFocusOnRow : (rowObj: any, bShift: boolean, bCtrl: boolean, onClick: boolean, onRightClick: boolean) => {return true;},
        hierarchyPagingArrowDisplayOverride:  (null != props.hierarchyPagingArrowDisplayOverride) ? props.hierarchyPagingArrowDisplayOverride : null,
        leftOfArrowContent: (null != props.leftOfArrowContent) ? props.leftOfArrowContent : null,
        overrideScrollIntoView: (null != props.overrideScrollIntoView) ? props.overrideScrollIntoView : null,
        scrollWithScrollBarAfterPaging: (null != props.scrollWithScrollBarAfterPaging) ? props.scrollWithScrollBarAfterPaging : true,
        onKeyUp: (null != props.onKeyUp) ? props.onKeyUp : null,
        stopPropagationOnKeyPress: (null != props.stopPropagationOnKeyPress) ? props.stopPropagationOnKeyPress : null,
        onCollapseExpandAccordion: (null != props.onCollapseExpandAccordion) ? props.onCollapseExpandAccordion : null,
        initialRowsSelected: (null != props.initialRowsSelected) ? props.initialRowsSelected : [],
        enableMultiSelect: (null != props.enableMultiSelect) ? props.enableMultiSelect : false,
        selectRecordsWithRowDrag: (null != props.selectRecordsWithRowDrag) ? props.selectRecordsWithRowDrag : false,
    };

    return <ReactHierarchicalList {...setProps as any} ref={refCallback} />;
}


/**
 * Creates an Orchard ReactJSONHierarchy on the element ID found
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function createReactJSONHierarchy(
    id: string,
    props: any,
    refCallback?: (component: ReactJSONHierarchy) => void
) {
    ReactDOM.render(
        getReactJSONHierarchy(props, refCallback),
        document.getElementById(id) as HTMLElement
    );
}

/**
 * Gets an Orchard ReactJSONHierarchy Object
 * @param id The id of the div element to build the data table in
 * @param options The options object
 * @param refCallback A callback executed once rendering is complete, providing a reference to the ReactTable component
 */
export function getReactJSONHierarchy(
    props: any,
    refCallback?: (component: ReactJSONHierarchy) => void
) {
    const setProps = {
        id: (null != props.id) ? props.id : "",
        hierarchyKey: (null != props.hierarchyKey) ? props.hierarchyKey : "",
        style: (null != props.style) ? props.style : {margin: "0px"},
        css: (null != props.css) ? props.css : 'react-hierarchy',
        divContainerCSS: (null != props.divContainerCSS) ? props.divContainerCSS : 'overFlowAuto',
        divContainerStyle: (null != props.divContainerStyle) ? props.divContainerStyle : {},
        expandedCSS: (null != props.expandedCSS) ? props.expandedCSS : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => 'flex-container-inline',
        expandedStyle: (null != props.expandedStyle) ? props.expandedStyle : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => {return {marginBottom: "0px", marginTop: "0px", fontWeight: "normal"};},
        contentStyle: (null != props.contentStyle) ? props.contentStyle : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => {return {marginTop: '0px', paddingLeft: '16px', fontWeight: "normal"};},
        contentCSS: (null != props.contentCSS) ? props.contentCSS : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => {return ""},
        dataKey: (null != props.dataKey) ? props.dataKey : "isExpanded",
        tabIndex: (null != props.tabIndex) ? props.tabIndex : null,
        refreshID: (null != props.refreshID) ? props.refreshID : "",
        onRefresh: (null != props.onRefresh) ? props.onRefresh : null,
        expandedID: (null != props.expandedID) ? props.expandedID : "",
        stopPropagationOnClick: (null != props.stopPropagationOnClick) ? props.stopPropagationOnClick : false,
        disableID: (null != props.disableID) ? props.disableID : "",
        rowID: (null != props.rowID) ? props.rowID : "",
        deleteButtonID: (null != props.deleteButtonID) ? props.deleteButtonID : "",
        hiddenTotalCountID: (null != props.hiddenTotalCountID) ? props.hiddenTotalCountID : "",
        disabled: (null != props.disabled) ? props.disabled : false,
        arrowExpandCSS: (null != props.arrowExpandCSS) ? props.arrowExpandCSS : () => "ui-accordion-header-icon ui-icon ui-icon-triangle-1-s",
        arrowCollapseCSS: (null != props.arrowCollapseCSS) ? props.arrowCollapseCSS : () => "ui-accordion-header-icon ui-icon ui-icon-triangle-1-e",
        arrowStyle: (null != props.arrowStyle) ? props.arrowStyle : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => {return {width: "16px", marginTop: "1px"};},
        isSelectedKey: (null != props.isSelectedKey) ? props.isSelectedKey : "isSelected",
        displayKey: (null != props.displayKey) ? props.displayKey : "",
        displayOverride: (null != props.displayOverride) ? props.displayOverride : null,
        jsonData: (null != props.jsonData) ? props.jsonData : {},
        hightlightSelectedRowCSS: (null != props.hightlightSelectedRowCSS) ? props.hightlightSelectedRowCSS : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => {return "copia_color3_b";},
        rowAdditionalAttributes: (null != props.rowAdditionalAttributes) ? props.rowAdditionalAttributes : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => {return {};},
        collapsedStyle: (null != props.collapsedStyle) ? props.collapsedStyle : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => {return {marginBottom: "0px", marginTop: "0px", fontWeight: "normal"};},
        collapsedCSS: (null != props.collapsedCSS) ? props.collapsedCSS : () => "flex-container-inline",
        rowsCSS: (null != props.rowsCSS) ? props.rowsCSS : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => {return 'flex-container-inline';},
        rowsStyle: (null != props.rowsStyle) ? props.rowsStyle : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => {return {height: '16px'};},
        cellsCSS: (null != props.cellsCSS) ? props.cellsCSS : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => {return 'flex-container-inline';},
        cellsStyle: (null != props.cellsStyle) ? props.cellsStyle : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => {return {height: '16px'};},
        onRowClick: (null != props.onRowClick) ? props.onRowClick : (rowObj : any, key: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number, shiftKey: boolean, ctrlKey: boolean) => {
            const dataKey = (null != props.dataKey) ? props.dataKey : "isExpanded";
            const isExpanded = null != rowObj[dataKey] && rowObj[dataKey];
            return !isExpanded;
        },
        onRowRightClick: (null != props.onRowRightClick) ? props.onRowRightClick : null,
        onKeyDown: (null != props.onKeyDown) ? props.onKeyDown : null,
        onRowDelete: (null != props.onRowDelete) ? props.onRowDelete : null,
        onAfterRowDelete: (null != props.onAfterRowDelete) ? props.onAfterRowDelete : null,
        addDeleteButton: (null != props.addDeleteButton) ? props.addDeleteButton : false,
        deleteButtonTabIndex: (null != props.deleteButtonTabIndex) ? props.deleteButtonTabIndex : null,
        addSortIcon: (null != props.addSortIcon) ? props.addSortIcon : false,
        addDropIcon: (null != props.addDropIcon) ? props.addDropIcon : false,
        enableDragAndDrop: (null != props.enableDragAndDrop) ? props.enableDragAndDrop : false,
        onDropEvent: (null != props.onDropEvent) ? props.onDropEvent : null,
        enableUnboundDragAndDrop: (null != props.enableUnboundDragAndDrop) ? props.enableUnboundDragAndDrop : false,
        allowDrag: (null != props.allowDrag) ? props.allowDrag : null,
        allowDrop: (null != props.allowDrop) ? props.allowDrop : null,
        allowDropToTheBottom: (null != props.allowDropToTheBottom) ? props.allowDropToTheBottom : true,
        allowExpandAndCollapse: (null != props.allowExpandAndCollapse) ? props.allowExpandAndCollapse : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number, isExpandingAll: boolean, isCollapsingAll: boolean) => {return true;},
        shouldRowUpdate: (null != props.shouldRowUpdate) ? props.shouldRowUpdate : null,
        initialRowSelected: (null != props.initialRowSelected) ? props.initialRowSelected : "",
        hierarchyNumToDisplay: (null != props.hierarchyNumToDisplay) ? props.hierarchyNumToDisplay : -1,
        rowKey: (null != props.rowKey) ? props.rowKey : null,
        onFocusEndKeyPressEvent: (null != props.onFocusEndKeyPressEvent) ? props.onFocusEndKeyPressEvent : null,
        allowFocusOnRow: (null != props.allowFocusOnRow) ? props.allowFocusOnRow : (rowObj: any, bShift: boolean, bCtrl: boolean, onClick: boolean, onRightClick: boolean) => {return true;},
        hierarchyPagingArrowDisplayOverride:  (null != props.hierarchyPagingArrowDisplayOverride) ? props.hierarchyPagingArrowDisplayOverride : null,
        leftOfArrowContent: (null != props.leftOfArrowContent) ? props.leftOfArrowContent : null,
        overrideScrollIntoView: (null != props.overrideScrollIntoView) ? props.overrideScrollIntoView : null,
        scrollWithScrollBarAfterPaging: (null != props.scrollWithScrollBarAfterPaging) ? props.scrollWithScrollBarAfterPaging : true,
        onKeyUp: (null != props.onKeyUp) ? props.onKeyUp : null,
        stopPropagationOnKeyPress: (null != props.stopPropagationOnKeyPress) ? props.stopPropagationOnKeyPress : null,
        onCollapseExpandAccordion: (null != props.onCollapseExpandAccordion) ? props.onCollapseExpandAccordion : null,
        initialRowsSelected: (null != props.initialRowsSelected) ? props.initialRowsSelected : [],
        enableMultiSelect: (null != props.enableMultiSelect) ? props.enableMultiSelect : false,
        selectRecordsWithRowDrag: (null != props.selectRecordsWithRowDrag) ? props.selectRecordsWithRowDrag : false,
        canDisplayRow: (null != props.canDisplayRow) ? props.canDisplayRow : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => true,
        canDisplayRowType: (null != props.canDisplayRowType) ? props.canDisplayRowType : (rowObj: any, jsonKey: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number) => true,
        onScroll: (null != props.onScroll) ? props.onScroll : null,
        listArrayContentInRows: (null != props.listArrayContentInRows) ? props.listArrayContentInRows : false,
        sortKeys: (null != props.sortKeys) ? props.sortKeys : null,
        displayLeafNode: (null != props.displayLeafNode) ? props.displayLeafNode : true,
        getJSONData: (null != props.getJSONData) ? props.getJSONData : null,
        enableKeyboardNavigation: (null != props.enableKeyboardNavigation) ? props.enableKeyboardNavigation : false,
        focusFirstElementOnLoad: (null != props.focusFirstElementOnLoad) ? props.focusFirstElementOnLoad : false,
        canFocusOnElement: (null != props.canFocusOnElement) ? props.canFocusOnElement : (element: HTMLElement, keyPressed: string, bShift: boolean, bCtrl: boolean, onClick: boolean, onRightClick: boolean) => {return true;},
    };

    return <ReactJSONHierarchy {...setProps as any} ref={refCallback} />;
}

export {
    ReactTable,
    ReactAccordion,
    ReactButton,
    ReactInput,
    ReactCheckbox,
    ReactLink,
    ReactSelect,
    ReactSelectOption,
    ReactSearchAsYouType,
    ReactMultiSelectSearchAsYouType,
    ReactCustomObject,
    ReactHierarchicalList,
    ReactJSONHierarchy,
    ECPHierarchyList,
    ECPHierarchyProps,
    createECPHierarchyList,
    getECPHierarchyList,
};
